import { useState } from 'react';

import { useFeedArticles } from '@/entities/feed-article';
import type { MonthlyRecapsArticle } from '@/features/monthly-recaps';

import { mapFeedInfoToArticle } from '@/features/markets/components/MonthlyRecapArticle/mapFeedInfoToArticle';
import { MonthlyRecapArticleModal } from '@/features/markets/components/MonthlyRecapArticle/MonthlyRecapArticleModal';
import { FeedSkeleton } from '../Feed/components/FeedSkeleton';
import { MonthlyRecapsListView } from './MonthlyRecapsListView';
import { MonthlyRecapsSingleView } from './MonthlyRecapsSingleView';

import type { MonthlyRecapsWidgetFilters } from './types';

export type { MonthlyRecapsWidgetFilters } from './types';

const CENTERED_STATE_CLASSNAME =
  'flex min-h-[160px] flex-1 items-center justify-center text-center text-muted-foreground';

const MonthlyRecapsEmptyState = () => (
  <div className={CENTERED_STATE_CLASSNAME}>
    <p>No monthly recap articles available.</p>
  </div>
);

const MonthlyRecapsErrorState = () => (
  <div className={CENTERED_STATE_CLASSNAME}>
    <p>Failed to load monthly recap articles.</p>
  </div>
);

const toFeedKeyCategories = (keyCategory: string | string[] | undefined): string[] => {
  if (Array.isArray(keyCategory)) return keyCategory;
  return keyCategory ? [keyCategory] : [];
};

export const MonthlyRecapsWidget = ({ metadata }: { metadata: MonthlyRecapsWidgetFilters }) => {
  const [openArticle, setOpenArticle] = useState<MonthlyRecapsArticle | null>(null);
  const feedKeyCategories = toFeedKeyCategories(metadata.KeyCategory);
  const { data, isLoading, isSuccess, isError } = useFeedArticles(feedKeyCategories);

  const articles = (data ?? [])
    .filter(item => !metadata.KeyValue || item.symbol.KeyValue === metadata.KeyValue)
    .toSorted(
      (a, b) => new Date(b.transactionTime).getTime() - new Date(a.transactionTime).getTime()
    )
    .map(mapFeedInfoToArticle);

  const hasNoData = isSuccess && articles.length === 0;

  return (
    <div className='flex max-h-[650px] min-h-[200px] flex-col overflow-y-auto'>
      {isLoading && <FeedSkeleton />}
      {hasNoData && <MonthlyRecapsEmptyState />}
      {isError && <MonthlyRecapsErrorState />}

      {!isLoading &&
        !hasNoData &&
        !isError &&
        (metadata.mode === 'single' ? (
          articles[0] && (
            <MonthlyRecapsSingleView
              article={articles[0]}
              onSelectArticle={setOpenArticle}
            />
          )
        ) : (
          <MonthlyRecapsListView
            articles={articles}
            onSelectArticle={setOpenArticle}
          />
        ))}

      {openArticle && (
        <MonthlyRecapArticleModal
          article={openArticle}
          onClose={() => {
            setOpenArticle(null);
          }}
        />
      )}
    </div>
  );
};
